/**
 * Dahua catalog — NVRs, hybrid HDCVI XVRs, and ARC AirShield wireless
 * alarm peripherals.
 *
 * Every entry below is verified against a primary Dahua datasheet PDF
 * fetched on 2026-05-14 from dahuasecurity.com / material.dahuasecurity.com.
 * The exact source URL appears as a comment above each entry. Fields are
 * left undefined when the datasheet does not list a value — never guessed.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // NVRs — Lite 4000 4KS3 Series (no AI chip — SMD Plus by recorder only)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/uploads/cpq/.../NVR4108HS-8P-4KS3 (read 2026-05-14)
  {
    id: 'dahua-nvr4108hs-8p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-8P-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite NVR — 8-port PoE (72 W), 1 SATA (20 TB), 12 MP IP support',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/soft/20250304/NVR4108HS-8P-4KS3_S0_datasheet_202301027.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR4216-16P-4KS3 (read 2026-05-14)
  {
    id: 'dahua-nvr4216-16p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4216-16P-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 1U NVR — 16-port PoE (130 W), 2 SATA (20 TB each), 12 MP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-4KS3_S0_datasheet_20231027.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR4232-4KS3 (read 2026-05-14)
  {
    id: 'dahua-nvr4232-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4232-4KS3',
    category: 'nvr-recorder',
    tagline: '32-ch Lite 1U NVR — no built-in PoE, 2 SATA (20 TB each), 12 MP support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-4KS3_S0_datasheet_20231113.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NVRs — WizSense 4000-EI Series (AI chip onboard: face / perimeter / SMD)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/.../NVR4216-EI (read 2026-05-14)
  {
    id: 'dahua-nvr4216-ei',
    manufacturer: 'Dahua',
    model: 'NVR4216-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense NVR — no built-in PoE, 2 SATA (16 TB each), AI by NVR',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-EI_S0_datasheet_20230628.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR4216-16P-EI (read 2026-05-14)
  {
    id: 'dahua-nvr4216-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4216-16P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense NVR — 16-port PoE (130 W), 2 SATA (16 TB), AI face + perimeter',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR4416-EI (read 2026-05-14)
  {
    id: 'dahua-nvr4416-ei',
    manufacturer: 'Dahua',
    model: 'NVR4416-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1.5U NVR — 4 SATA (16 TB), dual GbE, no built-in PoE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4416-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U', // Datasheet labels as 1.5U; rounded up.
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NVRs — WizSense 5000-EI Series (AcuPick, 4-HDD, higher bandwidth)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/.../NVR5216-16P-EI (read 2026-05-14)
  {
    id: 'dahua-nvr5216-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5216-16P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — 16-port PoE (130 W) with ePoE, 2 SATA (20 TB), AcuPick',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5216-16P-EI_S0_datasheet_20240625.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR5432-16P-EI (read 2026-05-14)
  {
    id: 'dahua-nvr5432-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR5432-16P-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 16-port PoE (150 W), 4 SATA (16 TB), 8K HDMI',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR5432-16P-EI_S0_datasheet_20230217.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 384,
      outputBandwidthMbps: 384,
      maxResolution: '32 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '2U', // Datasheet labels as 1.5U; rounded up.
      alarmIO: '16 in / 6 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // NVRs — WizMind 600-XI enterprise (8-HDD, RAID, 2× 2.5G NIC, ANPR)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/.../NVR608H-64-XI (read 2026-05-14)
  {
    id: 'dahua-nvr608h-64-xi',
    manufacturer: 'Dahua',
    model: 'NVR608H-64-XI',
    category: 'nvr-recorder',
    tagline: '64-ch WizMind 2U NVR — 8 SATA (16 TB), RAID 0/1/5/6/10, hot-swap, 2.5 GbE',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608H-64-XI_S0_datasheet_20230322.pdf',
    specs: {
      channels: 64,
      inputBandwidthMbps: 1024,
      outputBandwidthMbps: 800,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // https://material.dahuasecurity.com/.../NVR608RH-128-XI (read 2026-05-14)
  {
    id: 'dahua-nvr608rh-128-xi',
    manufacturer: 'Dahua',
    model: 'NVR608RH-128-XI',
    category: 'nvr-recorder',
    tagline: '128-ch WizMind 2U NVR — redundant PSU, 8 SATA, RAID 0/1/5/6/10, AcuPick 64ch',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR608RH-128-XI_S0_datasheet_20231021.pdf',
    specs: {
      channels: 128,
      inputBandwidthMbps: 1280,
      outputBandwidthMbps: 1024,
      maxResolution: '32 MP',
      hddBays: 8,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      raidLevels: ['0', '1', '5', '6', '10'],
      onvif: true,
      formFactor: '2U',
      alarmIO: '16 in / 8 out',
      networkPorts: '2× 10/100/1000/2500 RJ-45',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // HYBRID HDCVI DVRs — Penta-brid (HDCVI / AHD / TVI / CVBS / IP)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/.../XVR5108HS-4KL-I3 (read 2026-05-14)
  {
    id: 'dahua-xvr5108hs-4kl-i3',
    manufacturer: 'Dahua',
    model: 'XVR5108HS-4KL-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K WizSense DVR — HDCVI/AHD/TVI/CVBS + 8 IP, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5108HS-4KL-I3_V2_datasheet_20230720.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 128,
      outputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      networkPorts: '1× 10/100/1000 RJ-45',
      hybrid: true,
    },
  },

  // https://www.dahuasecurity.com/.../DH-XVR7208-16A-4KL-X (read 2026-05-14)
  {
    id: 'dahua-xvr7216a-4kl-x',
    manufacturer: 'Dahua',
    model: 'XVR7216A-4KL-X',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K Pro DVR — 16 analog + 16 IP overlay, 2 SATA (10 TB), IoT/POS',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/product/20180308/DH-XVR7208-16A-4KL-X_Datasheet_20180306.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '16 in / 3 out',
      networkPorts: '1× 1000M RJ-45',
      hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ARC ALARM — Hub (AirShield wireless control panel)
  // ─────────────────────────────────────────────────────────────────────

  // https://material.dahuasecurity.com/.../ARC3000H-GW2(868) (read 2026-05-14)
  {
    id: 'dahua-arc3000h-gw2',
    manufacturer: 'Dahua',
    model: 'ARC3000H-GW2(868)',
    category: 'alarm-panel',
    tagline: 'AirShield wireless hub — 150 peripherals, Ethernet + Wi-Fi + dual-SIM 2G, AES128',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/ARC3000H-GW2(868)_datasheet_20220628.pdf',
    specs: {
      series: 'AirShield',
      zonesOnboard: 0,
      zonesMax: 150,
      partitions: 1,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'DMSS',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ARC ALARM — Wireless detectors
  // ─────────────────────────────────────────────────────────────────────

  // https://www.dahuasecurity.com/products/.../ARD323-W2(868) (read 2026-05-14)
  {
    id: 'dahua-ard323-w2',
    manufacturer: 'Dahua',
    model: 'ARD323-W2(868)',
    category: 'magnetic-contact',
    tagline: 'Wireless door/window reed contact — 868 MHz two-way, AES128, EN 50131 Grade 2',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Alarms/Wireless-Alarm/Detectors/ARD323-W2(868)',
    specs: {
      mountType: 'surface',
      housing: 'plastic',
      tamper: true,
      dimensionsMm: '100.2 × 20.8 × 20.3',
    },
  },

  // https://materialfile.dahuasecurity.com/.../ARD1233-W2 (read 2026-05-14)
  {
    id: 'dahua-ard1233-w2',
    manufacturer: 'Dahua',
    model: 'ARD1233-W2',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 90,
    mountingHeightDefaultM: 2.2,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // ≤18 kg per datasheet
    tagline: 'Wireless indoor PIR — 12 m / 90°, pet-immune ≤18 kg, 433 MHz two-way, 4-yr battery',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/ARD1233-W2_S0_datasheet_20250707.pdf',
  },

  // https://material.dahuasecurity.com/.../ARD1731-W2(868) (read 2026-05-14)
  {
    id: 'dahua-ard1731-w2',
    manufacturer: 'Dahua',
    model: 'ARD1731-W2(868)',
    category: 'outdoor-wall-pir',
    pattern: 'cone',
    rangeM: 12,
    hfovDeg: 86,
    mountingHeightDefaultM: 2.2,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true, // ≤18 kg per datasheet
    tagline: 'Wireless PIR-camera — 12 m / 86°, 2 MP JPEG snapshots on event, 868 MHz two-way',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/ARD1731-W2(868)_datasheet_20230912.pdf',
  },

  // ─────────────────────────────────────────────────────────────────────
  // ARC ALARM — Sirens
  // ─────────────────────────────────────────────────────────────────────

  // https://www.dahuasecurity.com/products/.../ARA12-W2 (read 2026-05-14)
  {
    id: 'dahua-ara12-w2',
    manufacturer: 'Dahua',
    model: 'ARA12-W2',
    category: 'indoor-sounder',
    tagline: 'Wireless indoor sounder — ≥105 dB @ 1 m, 433 MHz two-way, 2× CR123A (2.5-yr standby)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Alarms/Wireless-Products/Accessories/ARA12-W2',
    specs: {
      // Datasheet does not specify transducer type; 'piezo' is typical for
      // this compact form factor — verify if you need precise SPL behaviour.
      acoustic: 'piezo',
      optical: 'led',
      spLAt1mDb: 105,
      batteryBackup: 'built-in',
    },
  },

  // https://materialfile.dahuasecurity.com/.../DHI-ARA13-W2 (read 2026-05-14)
  {
    id: 'dahua-ara13-w2',
    manufacturer: 'Dahua',
    model: 'ARA13-W2',
    category: 'outdoor-siren',
    tagline: 'Wireless outdoor siren — 110 dB @ 1 m, IP65, 12 LED groups, 433 MHz two-way, 4× CR123A',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/DHI-ARA13-W2_datasheet_20250715.pdf',
    specs: {
      acoustic: 'piezo',
      optical: 'led-bar',
      spLAt1mDb: 110,
      batteryBackup: 'built-in',
      ipRating: 'IP65',
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // ARC ALARM — Wireless keypad
  // ─────────────────────────────────────────────────────────────────────

  // https://www.dahuasecurity.com/products/.../ARK30T-W2 (read 2026-05-14)
  {
    id: 'dahua-ark30t-w2',
    manufacturer: 'Dahua',
    model: 'ARK30T-W2',
    category: 'keypad',
    tagline: 'Wireless keypad — 15 keys + 3 panic shortcuts, IC-card arm/disarm, 433 MHz, 4× AA (3 yr)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Alarms/Wireless-Alarm/Accessories/ARK30T-W2',
    specs: {
      display: 'led',
      connection: 'wireless-airshield',
      readerType: 'mifare',
      compatibility: ['AirShield'],
    },
  },
  // SRC: Dahua "PFT1200 High PoE Midspan" datasheet (2012): "Support high PoE
  //      60W", "1*10/100/1000 Base-T、1*10/100/1000 Base-T( PoE power supply)",
  //      "Installation mode: wall mount", "﹣30℃～65℃", "390g",
  //      "150mm× 70mm× 39mm". No IEEE class and no mains rating on the sheet.
  {
    id: 'dahua-pft1200',
    manufacturer: 'Dahua',
    model: 'PFT1200',
    category: 'poe-injector',
    tagline: 'Single-port Hi-PoE midspan, 60 W, gigabit pass-through, wall mount',
    verified: true,
    datasheetUrl: 'https://www.tssgroup.cz/buxus/docs/datasheety/Dahua%20PFT1200%20en%20datasheet.pdf',
    specs: {
      ports: 1,
      poeStandard: 'Hi-PoE',
      poePerPortW: 60,
      poeBudgetW: 60,
      dataSpeed: '1G',
      formFactor: 'brick',
      operatingTempC: '-30 to 65',
    },
  },
];
